import { ChartManager, DataRowExt, DataStack } from '../chart.manager';

import { D3PathFilledData, d3renderCircle, d3renderGradient, d3renderPath, d3renderPathFilled, d3renderRect, d3renderText } from '../chart.d3';
import { getCurve } from '../chart.utils';

import chroma from 'chroma-js';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import { clamp } from '@23degrees/shared-code/utilities';
import { callMoment } from '@23degrees/shared-code/utilities/date';
import { pointer, select } from 'd3-selection';
import { area, Series, SeriesPoint } from 'd3-shape';
import { overlayText } from '../chart.overlay';
import { color2css } from '../utilities/utils';

export const renderArea = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, valueField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, lines } = typeSpecific.chart;
  const { margin, padding } = typeSpecific.chart.style;
  const offsetTop = chartManager.getStyleInPx(margin, 'top') + chartManager.getStyleInPx(padding, 'top');
  const offsetLeft = chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');

  const getDimensionValue = (obj: DataStack) => {
    const { dataType } = typeSpecific.fields[aggregationField];
    if (dataType === 'date') return chartManager.xScale(obj['~meta'].moment);
    return chartManager.xScale(obj['~meta'].group) + chartManager.xScaleBandWidth() / 2;
  };

  const curve = getCurve(lines.curve);
  const areaFn = area<SeriesPoint<DataStack>>()
    .curve(curve)
    .x((obj) => getDimensionValue(obj.data))
    .y0((obj) => chartManager.yScale(0)) // obj[0]
    .y1((obj) => chartManager.yScale(Number.isNaN(obj[1]) ? obj[0] : obj[1]));

  const startFn = area<SeriesPoint<DataStack>>()
    .curve(curve)
    .x((obj) => getDimensionValue(obj.data))
    .y0((obj) => chartManager.yScale(0)) // obj[0]
    .y1((obj) => chartManager.yScale(0));

  let prevMoveData: DataRowExt;
  const getAreaData = (coordinates: [number, number], stack: Series<DataStack, string>) => {
    const { dataType } = typeSpecific.fields[aggregationField];

    let areaData: DataRowExt;
    const coordX = coordinates[0] - chartManager.yAxisWidth - offsetLeft;

    if (dataType === 'date') {
      const targetTime = callMoment(chartManager.xScale.invert(coordX));
      const diffMap = stack.map((obj) => ({ meta: obj.data['~meta'], diff: Math.abs(targetTime.diff(obj.data['~meta'].moment)) }));
      diffMap.sort((a, b) => a.diff - b.diff);
      const match = diffMap[0];
      areaData = match.meta.data.find((obj) => obj[labelField] === stack.key);
    } else {
      const onePart = chartManager.chartWidth / stack.length;
      const halfPart = onePart / 2;
      const stackWidth = onePart * (stack.length - 1);
      const idx = Math.floor(((coordX - halfPart) / stackWidth) * (stack.length - 1) + 0.5);
      const safeIdx = clamp(idx, 0, stack.length - 1);
      areaData = stack[safeIdx].data['~meta'].data.find((obj) => obj[labelField] === stack.key);
    }

    return areaData;
  };

  const data: D3PathFilledData[] = [];

  chartManager.dataStackPositive.reverse();
  chartManager.dataStackNegative.reverse();

  for (const dataStack of [...chartManager.dataStackPositive, ...chartManager.dataStackNegative]) {
    const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === dataStack.key);
    if (!group) continue;

    const color = chartManager.groupedColorScale(group['~meta']);
    const hoverColor = color2css(chroma(color).darken());

    const click = (event) => {
      const coordinates = pointer(event, chartManager.chart.element.node());
      const dataRef = getAreaData(coordinates, dataStack);

      // const x1 = getDimensionValue(dataRef) + chartManager.yAxisWidth + offsetLeft;
      // const y1 = chartManager.yScale(dataRef[valueField]) + offsetTop;
      chartManager.openPopup(event, dataRef, coordinates);

      chartManager.mouseClick(event, dataRef);
    };

    const enter = (event, _, node) => {
      select(node).attr('fill', hoverColor);

      const coordinates = pointer(event, chartManager.chart.element.node());
      const dataRef = getAreaData(coordinates, dataStack);

      chartManager.mouseEnter(event, dataRef);
    };

    const leave = (event, _, node) => {
      select(node).attr('fill', color);

      const coordinates = pointer(event, chartManager.chart.element.node());
      const dataRef = getAreaData(coordinates, dataStack);

      chartManager.mouseLeave(event, dataRef);
    };

    const move = (event, _, node) => {
      const coordinates = pointer(event, chartManager.chart.element.node());
      const dataRef = getAreaData(coordinates, dataStack);

      if (prevMoveData !== dataRef) {
        prevMoveData = dataRef;
        chartManager.mouseEnter(event, dataRef);
      }

      chartManager.mouseMove(event, dataRef);
    };

    const callbacks = { click, leave, enter, move };

    const path = areaFn(dataStack);
    const start = startFn(dataStack);
    const animate = {
      enabled: animation.enabled,
      delay: animation.delay,
      duration: animation.duration,
      color: color2css(color),
      path: path,
    };

    data.push({ color: color2css(color), path: start, animate, callbacks });
  }

  // clear
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderRect(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, data, '.chart', chartManager.animationStop);

  overlayText(chartManager.overlay, [], '.overlay-values');
};
